import { getSubunitMultiplier } from '@bbpos/utils';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  PageContainer,
  ErrorBoundary,
  Select,
  Button,
  DataTable,
  Skeleton,
  StatCard,
  EmptyState,
  Modal,
  Input,
  RadioGroup,
  DatePicker,
  useToast,
} from '@bbpos/ui';
import type { ColumnDef, DateRange } from '@bbpos/ui';
import { formatPaise } from '@/utils/currency';
import { ROLE_LEVELS } from '@/types/user';
import { useAuthStore } from '@/stores/use-auth-store';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { useTenantCurrency, getCurrencySymbol } from '@/hooks/use-tenant-currency';
import {
  useCreditInvoices,
  useApplyPayment,
  InvoiceStatusBadge,
} from '@/features/credits';
import type { CreditInvoice, InvoiceStatus, PaymentMethod } from '@/features/credits';
import { customersApi } from '@/features/customers';
import type { Customer } from '@/features/customers';

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'OUTSTANDING', label: 'Outstanding' },
  { value: 'PARTIALLY_PAID', label: 'Partially Paid' },
  { value: 'PAID', label: 'Paid' },
  { value: 'WRITTEN_OFF', label: 'Written Off' },
];

type Row = CreditInvoice;

function formatDateISO(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function formatDateIN(dateStr: string | null): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function CreditInvoicesContent() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const currency = useTenantCurrency();
  const [searchParams, setSearchParams] = useSearchParams();
  const flags = useFeatureFlag();
  const role = useAuthStore((s) => s.profile?.role ?? '');
  const roleLevel = ROLE_LEVELS[role] ?? 0;
  const canPay = roleLevel >= ROLE_LEVELS.OWNER;

  const page = Number(searchParams.get('page') ?? '1');
  const status = (searchParams.get('status') ?? '') as InvoiceStatus | '';
  const customerSearch = searchParams.get('customer') ?? '';
  const dateFrom = searchParams.get('date_from') ?? '';
  const dateTo = searchParams.get('date_to') ?? '';

  // DatePicker range state
  const dateRange = useMemo<DateRange | undefined>(() => {
    if (dateFrom && dateTo) return { from: new Date(dateFrom), to: new Date(dateTo) };
    return undefined;
  }, [dateFrom, dateTo]);

  const { data, isLoading, isFetching } = useCreditInvoices({
    page,
    status: status || undefined,
    customer_search: customerSearch || undefined,
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
  });
  const rows = useMemo<Row[]>(() => data?.data ?? [], [data]);

  const totalOutstanding = useMemo(() => rows.filter((r) => r.status !== 'PAID').reduce((s, r) => s + r.balance, 0), [rows]);
  const totalPaid = useMemo(() => rows.reduce((s, r) => s + r.paid_amount, 0), [rows]);
  const overdueCount = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return rows.filter((r) => r.balance > 0 && r.due_date && r.due_date < today).length;
  }, [rows]);

  const paymentMutation = useApplyPayment();
  const [payInvoiceId, setPayInvoiceId] = useState<string | null>(null);
  const [payAmountRupees, setPayAmountRupees] = useState('');
  const [payMethod, setPayMethod] = useState<PaymentMethod>('CASH');
  const [payRef, setPayRef] = useState('');
  const [payAmountError, setPayAmountError] = useState<string | undefined>();
  const payInvoice = useMemo(() => rows.find((r) => r.id === payInvoiceId), [rows, payInvoiceId]);

  // ── Customer search typeahead (3+ chars → suggestions) ──
  const tenantId = useAuthStore((s) => s.profile?.tenant_id ?? '');
  const [showSuggest, setShowSuggest] = useState(false);
  const [debouncedCustomer, setDebouncedCustomer] = useState('');
  const comboRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedCustomer(customerSearch.trim()), 250);
    return () => clearTimeout(t);
  }, [customerSearch]);

  const customerQuery = useQuery({
    queryKey: ['customers', tenantId, 'invoice-typeahead', debouncedCustomer],
    queryFn: () => customersApi.list({ page: 1, limit: 8, search: debouncedCustomer }),
    enabled: showSuggest && debouncedCustomer.length >= 3,
    staleTime: 30_000,
  });
  const suggestions =
    showSuggest && debouncedCustomer.length >= 3 ? (customerQuery.data?.data ?? []) : [];

  useEffect(() => {
    if (!showSuggest) return;
    function onDown(e: MouseEvent) {
      if (comboRef.current && !comboRef.current.contains(e.target as Node)) setShowSuggest(false);
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [showSuggest]);

  function setParam(key: string, value: string) {
    const params = new URLSearchParams(searchParams);
    if (value) params.set(key, value);
    else params.delete(key);
    if (key !== 'page') params.set('page', '1');
    setSearchParams(params);
  }

  function handleDateRangeChange(range: DateRange) {
    const params = new URLSearchParams(searchParams);
    params.set('date_from', formatDateISO(range.from));
    params.set('date_to', formatDateISO(range.to));
    params.set('page', '1');
    setSearchParams(params);
  }

  function clearDateRange() {
    const params = new URLSearchParams(searchParams);
    params.delete('date_from');
    params.delete('date_to');
    params.set('page', '1');
    setSearchParams(params);
  }

  useEffect(() => {
    if (!flags.ENABLE_CREDIT_BILLING) navigate('/dashboard', { replace: true });
  }, [flags, navigate]);

  const handlePayment = useCallback(() => {
    if (!payInvoiceId) return;
    const amtPaise = Math.round(Number(payAmountRupees) * getSubunitMultiplier(currency));
    if (!Number.isFinite(amtPaise) || amtPaise <= 0) { toast({ variant: 'warning', title: 'Invalid amount' }); return; }
    if (payInvoice && amtPaise > payInvoice.balance) { setPayAmountError(`Enter at most ${formatPaise(payInvoice.balance, currency)}`); return; }
    paymentMutation.mutate(
      { invoiceId: payInvoiceId, input: { amount: amtPaise, payment_method: payMethod, reference_number: payRef.trim() || undefined } },
      {
        onSuccess: () => { toast({ variant: 'success', title: 'Payment applied' }); setPayInvoiceId(null); setPayAmountRupees(''); setPayRef(''); },
        onError: (err) => { toast({ variant: 'error', title: 'Failed', description: (err as Error).message }); },
      },
    );
  }, [payInvoiceId, payInvoice, payAmountRupees, payMethod, payRef, paymentMutation, toast, currency]);

  const columns: ColumnDef<Row>[] = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    const cols: ColumnDef<Row>[] = [
      { key: 'invoice_number' as keyof Row, header: 'Invoice #', render: (_v: unknown, row: Row) => <span className="text-sm font-bold text-neutral-900 dark:text-neutral-100">{row.invoice_number}</span> },
      { key: 'customer_name' as keyof Row, header: 'Customer', render: (_v: unknown, row: Row) => <span className="text-sm text-neutral-700 dark:text-neutral-200">{row.customer_name ?? '—'}</span> },
      { key: 'amount' as keyof Row, header: 'Amount', numeric: true, render: (_v: unknown, row: Row) => <span className="text-sm font-bold tabular-nums">{formatPaise(row.amount, currency)}</span> },
      { key: 'paid_amount' as keyof Row, header: 'Paid', numeric: true, render: (_v: unknown, row: Row) => <span className="text-sm tabular-nums text-success-600 dark:text-success-400">{formatPaise(row.paid_amount, currency)}</span> },
      { key: 'balance' as keyof Row, header: 'Balance', numeric: true, render: (_v: unknown, row: Row) => <span className={`text-sm font-bold tabular-nums ${row.balance > 0 ? 'text-danger-600 dark:text-danger-400' : ''}`}>{formatPaise(row.balance, currency)}</span> },
      { key: 'status' as keyof Row, header: 'Status', render: (_v: unknown, row: Row) => <InvoiceStatusBadge status={row.status} /> },
      { key: 'due_date' as keyof Row, header: 'Due Date', render: (_v: unknown, row: Row) => {
        const overdue = row.due_date && row.due_date < today && row.balance > 0;
        return <span className={`text-xs tabular-nums ${overdue ? 'text-danger-600 dark:text-danger-400 font-bold' : 'text-neutral-500 dark:text-neutral-400'}`}>{formatDateIN(row.due_date)}</span>;
      }},
      { key: 'created_at' as keyof Row, header: 'Created', render: (_v: unknown, row: Row) => <span className="text-xs text-neutral-500 dark:text-neutral-400 tabular-nums">{formatDateIN(row.created_at)}</span> },
    ];
    if (canPay) {
      cols.push({ key: 'actions' as keyof Row, header: '', render: (_v: unknown, row: Row) => row.status !== 'PAID' && row.status !== 'WRITTEN_OFF' ? <Button variant="secondary" onClick={() => { setPayInvoiceId(row.id); setPayAmountRupees(''); setPayRef(''); setPayAmountError(undefined); }} className="text-xs px-2 py-1">Pay</Button> : null });
    }
    return cols;
  }, [canPay, currency]);

  if (!flags.ENABLE_CREDIT_BILLING) return null;

  return (
    <div className="animate-fade-in">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-500 p-6 sm:p-7 text-white mb-6 shadow-[0_25px_70px_-25px_rgba(16,185,129,0.7)]">
        <div className="pointer-events-none absolute -top-24 -right-16 h-56 w-56 rounded-full bg-white/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-16 -left-10 h-48 w-48 rounded-full bg-black/15 blur-3xl" />
        <div className="relative">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-[10px] font-bold uppercase tracking-wider ring-1 ring-white/25 backdrop-blur-sm">
            Collections
          </span>
          <h1 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight text-white">Credit Invoices</h1>
          <p className="mt-1.5 text-sm text-white/90 max-w-xl">
            Track all credit invoices, apply payments, and monitor overdue balances.
          </p>
        </div>
      </div>

      {/* KPI Cards — using StatCard */}
      {isLoading ? (
        <div className="grid grid-cols-3 gap-4 mb-6">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} variant="rect" height={100} className="rounded-xl" />)}</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <StatCard
            title="Total Outstanding"
            value={formatPaise(totalOutstanding, currency)}
            variant="default"
            icon={<svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
          />
          <StatCard
            title="Total Paid"
            value={formatPaise(totalPaid, currency)}
            variant="default"
            icon={<svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
          />
          <StatCard
            title="Overdue Invoices"
            value={String(overdueCount)}
            variant="default"
            icon={<svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>}
            anomaly={overdueCount > 0}
          />
        </div>
      )}

      {/* Filters — status, customer search, date range */}
      {/* relative z-30: lift this card's stacking context above the sibling
          cards below (which use backdrop-blur and would otherwise paint over
          the open dropdowns, washing them out). */}
      <div className="relative z-30 rounded-xl backdrop-blur-sm bg-white/80 dark:bg-neutral-800/80 shadow-md p-4 sm:p-5 mb-6">
        <div className="flex flex-col sm:flex-row gap-3 items-end">
          <div className="w-full sm:w-44">
            <Select options={STATUS_OPTIONS} value={status} onChange={(v) => setParam('status', typeof v === 'string' ? v : '')} aria-label="Filter by status" />
          </div>
          <div className="flex-1 relative" ref={comboRef}>
            <Input
              placeholder="Type 3+ letters to search customer…"
              value={customerSearch}
              onChange={(v) => { setParam('customer', v); setShowSuggest(true); }}
              onKeyDown={(e) => { if (e.key === 'Escape') setShowSuggest(false); }}
              aria-label="Search by customer"
            />
            {showSuggest && debouncedCustomer.length >= 3 && (
              <div className="absolute z-50 mt-1 w-full rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 shadow-lg max-h-60 overflow-y-auto">
                {customerQuery.isLoading ? (
                  <div className="px-3 py-2 text-sm text-neutral-500 dark:text-neutral-400">Searching…</div>
                ) : suggestions.length === 0 ? (
                  <div className="px-3 py-2 text-sm text-neutral-500 dark:text-neutral-400">No matching customers</div>
                ) : (
                  suggestions.map((c: Customer) => (
                    <button
                      type="button"
                      key={c.id}
                      onClick={() => { setParam('customer', c.name); setShowSuggest(false); }}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-emerald-50 dark:hover:bg-neutral-700 flex items-center justify-between gap-2"
                    >
                      <span className="font-medium text-neutral-900 dark:text-neutral-100 truncate">{c.name}</span>
                      <span className="text-xs font-mono tabular-nums text-neutral-500 dark:text-neutral-400 shrink-0">{c.phone}</span>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
          <div className="w-full sm:w-56">
            <DatePicker variant="range" label="Date Range" value={dateRange} onChange={handleDateRangeChange} />
          </div>
          {(dateFrom || dateTo) ? (
            <Button variant="secondary" onClick={clearDateRange} className="text-xs shrink-0">Clear dates</Button>
          ) : null}
        </div>
      </div>

      {/* Data */}
      {isLoading ? (
        <div className="space-y-2">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} variant="rect" height={56} className="rounded-lg" />)}</div>
      ) : rows.length === 0 && !isFetching ? (
        <div className="rounded-2xl backdrop-blur-sm bg-white/80 dark:bg-neutral-800/80 shadow-md p-10 animate-fade-in">
          <EmptyState variant="page" title="No credit invoices" description="Credit invoices will appear here when credit sales are made."
            icon={<svg className="h-20 w-20 text-emerald-300 dark:text-emerald-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.3}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>}
          />
        </div>
      ) : (
        <section className="rounded-2xl backdrop-blur-sm bg-white/80 dark:bg-neutral-800/80 shadow-md overflow-hidden animate-slide-up">
          <DataTable<Row> columns={columns} data={rows} loading={isFetching} totalCount={data?.meta.total} currentPage={page} pageSize={20} onPageChange={(p) => setParam('page', String(p))} stickyHeader />
        </section>
      )}

      {/* Payment Modal — accepts rupees */}
      <Modal open={Boolean(payInvoiceId)} onClose={() => setPayInvoiceId(null)} title="Apply Payment" variant="wide">
        <div className="space-y-4">
          <Input label={`Amount (${getCurrencySymbol(currency)})`} type="number" min={0.01} step={0.01} value={payAmountRupees} onChange={(v) => { setPayAmountRupees(v); setPayAmountError(undefined); }} error={payAmountError} helperText="Enter amount in rupees" required />
          <RadioGroup label="Payment Method" value={payMethod} onChange={(v) => setPayMethod(v as PaymentMethod)} variant="horizontal"
            options={[
              { value: 'CASH', label: 'Cash' },
              { value: 'UPI', label: 'UPI' },
              { value: 'CARD', label: 'Card' },
              { value: 'BANK_TRANSFER', label: 'Bank Transfer' },
            ]}
          />
          <Input label="Reference Number" value={payRef} onChange={setPayRef} placeholder="TXN-12345 (optional)" />
          <div className="flex items-center justify-end gap-3 pt-2">
            <Button variant="secondary" onClick={() => setPayInvoiceId(null)}>Cancel</Button>
            <Button variant="primary" onClick={handlePayment} loading={paymentMutation.isPending}>Apply Payment</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export function CreditInvoicesListPage() {
  return (
    <PageContainer title="Credit Invoices" breadcrumbs={[{ label: 'Home', href: '/home' }, { label: 'Credit Invoices' }]}>
      <ErrorBoundary resetKey="credit-invoices-list">
        <CreditInvoicesContent />
      </ErrorBoundary>
    </PageContainer>
  );
}
